import { TagSelectionField } from '@src/entities/Tag/ui/components/fields/TagSelection';
import tags from '@src/entities/Tag/ui/components/fields/TagSelection/includes/configuration.json';
import { fieldValidator } from '@src/shared/includes/validation';
import { Button } from '@src/shared/ui/components/Button';
import { DateRange } from '@src/shared/ui/components/DateRange';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { dayjs } from '@uhg-abyss/web/tools/dayjs';
import { Flex } from '@uhg-abyss/web/ui/Flex';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { RadioGroup } from '@uhg-abyss/web/ui/RadioGroup';
import { SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { SelectInputMulti } from '@uhg-abyss/web/ui/SelectInputMulti';
import { TextInput } from '@uhg-abyss/web/ui/TextInput';
import { isEmpty } from 'lodash';
import React, { useEffect, useMemo } from 'react';

import fields from './includes/fields.json';
import { getCategoryOptions } from './includes/functions';
import { Styles } from './includes/styles';

const { frequency, tagCategories, tagVariantThreshold, tagVariantThresholdType } = fields;

/**
 * Filters
 *
 * Provides form fields to control the data returned
 *
 * @param assets
 * @param form
 * @returns {Element}
 * @constructor
 */
export function Filters({
  assets = {
    ListChronoUnits: {
      data: [],
    },
    ListTags: {
      data: {
        tagsList: {},
      },
    },
  },
  form = {
    formState: {
      errors: {},
    },
    getValues: undefined,
    setValue: undefined,
    validate: undefined,
  },
}) {
  const frequencyValue = form?.getValues('frequency');

  /**
   * tagCategories.options
   *
   * This is a memoized value that is used to populate the tagCategories options for the select input.
   *
   * @type {*[]}
   */
  tagCategories.options = useMemo(() => {
    return getCategoryOptions(assets?.ListTags?.data?.tagsList);
  }, [assets?.ListTags?.data?.tagsList]);

  /**
   *
   */
  const minimumDate = useMemo(() => {
    let date;

    if (['MONTHS', 'WEEKS'].includes(frequencyValue)) {
      date = dayjs(new Date()).subtract(2, 'year').format('MM/DD/YYYY');
    } else {
      date = dayjs(new Date()).subtract(6, 'months').format('MM/DD/YYYY');
    }

    return date;
  }, [frequencyValue]);

  /**
   * Date range restrictions.
   */
  useEffect(() => {
    const dateFrom = form?.getValues('dateRange.start');

    if (['MONTHS', 'WEEKS'].includes(frequencyValue)) {
      // Only allow maximum of 2 years in the past
      if (
        !dayjs(dateFrom).isBetween(
          dayjs(new Date()).subtract(2, 'years').format('MM/DD/YYYY'),
          dayjs(new Date()).format('MM/DD/YYYY'),
          'day'
        )
      ) {
        form?.setValue('dateRange.start', dayjs(new Date()).subtract(2, 'years').format('MM/DD/YYYY'));
      }
    }

    if (frequencyValue === 'DAYS') {
      // Only allow maximum of 6 months in the past
      if (
        !dayjs(dateFrom).isBetween(
          dayjs(new Date()).subtract(6, 'months').format('MM/DD/YYYY'),
          dayjs(new Date()).format('MM/DD/YYYY'),
          'day'
        )
      ) {
        form?.setValue('dateRange.start', dayjs(new Date()).subtract(6, 'months').format('MM/DD/YYYY'));
      }
    }
  }, [frequencyValue]);

  const fieldType = form.watch('type');

  /**
   * Validates the fields
   */
  useEffect(() => {
    if (fieldType === 'tags') {
      form?.validate(
        `tags`,
        () => {},
        () => {}
      );
    }

    if (fieldType === 'tagCategories') {
      form?.validate(
        `tagCategories`,
        () => {},
        () => {}
      );
    }

    form?.validate(
      `tagVariantThreshold`,
      () => {},
      () => {}
    );

    form?.validate(
      `frequency`,
      () => {},
      () => {}
    );

    form?.validate(
      `dateRange.start`,
      () => {},
      () => {}
    );

    form?.validate(
      `dateRange.end`,
      () => {},
      () => {}
    );
  }, [fieldType]);

  return (
    <ErrorHandler location="src/pages/private/Dashboards/TagHistoryDashboard/components/Filters/Filters.jsx">
      <Styles>
        <Grid css={{ position: 'relative' }}>
          <Grid.Col span={{ lg: '25%', md: '50%', sm: '50%', xs: '50%' }}>
            <div id="tags">
              <RadioGroup
                display="row"
                label=""
                model="type"
                onChange={(event) => {
                  const value = event?.target?.value;

                  if (value === 'tags') {
                    form?.setValue('tagCategories', []);
                  }

                  if (value === 'tagCategories') {
                    form?.setValue('tags', []);
                  }
                }}
                size="sm"
              >
                <RadioGroup.Radio label={tags?.label} value={tags?.model} />
                <RadioGroup.Radio label={tagCategories?.label} value={tagCategories?.model} />
              </RadioGroup>

              {fieldType === 'tags' && <TagSelectionField form={form} isRequired={false} showLabel={false} />}
              {fieldType === 'tagCategories' && (
                <SelectInputMulti
                  {...tagCategories}
                  label=""
                  onChange={() => {
                    form?.validate(
                      `tagCategories`,
                      () => {},
                      () => {}
                    );
                  }}
                />
              )}
            </div>
          </Grid.Col>
          <Grid.Col span={{ lg: '25%', md: '50%', sm: '50%', xs: '50%' }}>
            <TextInput
              {...tagVariantThreshold}
              leftAddOn={
                <SelectInput
                  {...tagVariantThresholdType}
                  options={[
                    {
                      label: 'Fixed',
                      value: 'fixed',
                    },
                    {
                      label: 'Percentage',
                      value: 'percentage',
                    },
                  ]}
                />
              }
              onChange={() => {
                form?.validate(
                  `tagVariantThreshold`,
                  () => {},
                  () => {}
                );
              }}
              validators={{
                ...tagVariantThreshold?.validators,
                ...{
                  validate: {
                    customValidator: (value) => {
                      return fieldValidator(tagVariantThreshold, value);
                    },
                  },
                },
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ lg: '25%', md: '50%', sm: '50%', xs: '50%' }}>
            <SelectInput
              {...frequency}
              onChange={() => {
                form?.validate(
                  `frequency`,
                  () => {},
                  () => {}
                );
              }}
              options={assets?.ListChronoUnits?.data
                ?.map((chronoUnit) => {
                  return {
                    label: chronoUnit?.codeDesc,
                    value: chronoUnit?.codeId,
                  };
                })
                .filter((chronoUnit) => {
                  return ['DAYS', 'MONTHS', 'WEEKS'].includes(chronoUnit?.value);
                })}
            />
          </Grid.Col>
          <Grid.Col span={{ lg: '25%', md: '50%', sm: '50%', xs: '50%' }}>
            <Grid css={{ paddingLeft: 0 }}>
              <Grid.Col css={{ paddingLeft: 0, paddingRight: 0 }} span={{ xs: '75%' }}>
                <DateRange
                  defaultEndDate={form?.getValues('dateRange.end')}
                  defaultStartDate={form?.getValues('dateRange.start')}
                  maximumDate={dayjs(new Date()).format('MM/DD/YYYY')}
                  minimumDate={minimumDate}
                  onChange={(event = {}) => {
                    form?.setValue('dateRange.start', String(event?.startDate), {
                      shouldDirty: true,
                      shouldValidate: true,
                    });

                    form?.setValue('dateRange.end', String(event?.endDate), {
                      shouldDirty: true,
                      shouldValidate: true,
                    });

                    form?.validate(
                      `dateRange.start`,
                      () => {},
                      () => {}
                    );

                    form?.validate(
                      `dateRange.end`,
                      () => {},
                      () => {}
                    );
                  }}
                />
              </Grid.Col>
              <Grid.Col span={{ xs: '25%' }}>
                <Flex
                  alignContent="flex-end"
                  alignItems="flex-end"
                  css={{ height: '100%' }}
                  direction="row"
                  justify="flex-start"
                >
                  <Button
                    isDisabled={!form?.formState?.isDirty || !isEmpty(form?.formState?.errors)}
                    type="submit"
                    variant="solid"
                  >
                    Apply
                  </Button>
                </Flex>
              </Grid.Col>
            </Grid>
          </Grid.Col>
        </Grid>
      </Styles>
    </ErrorHandler>
  );
}
